export const useSnackbar = () => {
  //
  // Configuration
  //
  const config = useRuntimeConfig();

  //
  // State
  //
  const snackbar = useState<boolean>("snackbar", () => false);
  const snackbarText = useState<string>("snackbarText", () => "");
  const snackbarColor = useState<string>("snackbarColor", () => "grey");
  const snackbarTimeout = useState<number>(
    "snackbarTimeout",
    () => config.public.ui.snackbarTimeout,
  );
  const snackbarIcon = useState<string | undefined>("snackbarIcon", () => undefined);
  const snackbarActionText = useState<string | undefined>("snackbarActionText", () => undefined);
  const snackbarActionHandler = useState<(() => void) | undefined>(
    "snackbarActionHandler",
    () => undefined,
  );

  /**
   * Monotonically increasing token used to prevent stale nextTick callbacks
   * from reopening the snackbar when showSnackbar is called rapidly.
   * Only the callback whose token matches the latest value will proceed.
   */
  let reopenToken = 0;

  //
  // Methods
  //

  /**
   * Display a snackbar with the given text and options.
   *
   * Forces a close → reopen cycle via `nextTick` so that Vuetify restarts
   * its internal countdown timer every time — even when the snackbar is
   * already visible from a previous call. Without this, a second call while
   * the snackbar is open inherits the remaining time and the new message
   * vanishes almost instantly.
   *
   * For rapid-fire calls only the **last** invocation wins: earlier nextTick
   * callbacks see a stale token and skip the reopen.
   */
  const showSnackbar = (
    text: string,
    options?: {
      color?: string;
      timeout?: number;
      icon?: string;
      /** Text for an optional action button inside the snackbar */
      actionText?: string;
      /** Callback when the action button is clicked */
      onAction?: () => void;
    },
  ) => {
    // Set content first so it's ready before the snackbar opens
    snackbarText.value = text;
    snackbarColor.value = options?.color || "grey";
    snackbarTimeout.value = options?.timeout || config.public.ui.snackbarTimeout;
    snackbarIcon.value = options?.icon;
    snackbarActionText.value = options?.actionText;
    snackbarActionHandler.value = options?.onAction;

    // Close the snackbar to force Vuetify to see a false→true transition.
    // This restarts the internal countdown timer from zero.
    snackbar.value = false;

    // Capture a unique token so only the latest call reopens the snackbar.
    // Earlier rapid-fire calls see a stale token and become no-ops.
    const myToken = ++reopenToken;
    nextTick(() => {
      if (myToken === reopenToken) {
        snackbar.value = true;
      }
    });
  };

  const hideSnackbar = () => {
    snackbar.value = false;
  };

  return {
    // State
    snackbar,
    snackbarText,
    snackbarColor,
    snackbarTimeout,
    snackbarIcon,
    snackbarActionText,
    snackbarActionHandler,

    // Methods
    showSnackbar,
    hideSnackbar,
  };
};
